import React, { useContext, useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { Button } from 'primereact/button';
import { MultiSelect } from 'primereact/multiselect';
import FileDropzone from 'components/FileDropzone/FileDropzone';
import { Auth } from 'aws-amplify';

import { useWebSocketContext, WebSocketConnectionContext } from 'context/WebSocketConnectionContext';
import { uploadFilesV2, UPLOAD_CATEGORY } from 'utils/filesUtils';
import { UserDetailsContext } from 'context/userDetailsContext';
import { useLoaderDispatch } from 'context/LoaderContext';
import constants from 'constants/index';
import { getTenantIdFromLS } from 'utils/localStorageUtils';

import InputErrorMessage from 'components/UI/InputErrorMessage/InputErrorMessage';
import { useSecureMessageContext } from 'modules/messages/SecureMessages/Context/SecureMessageContext';
import useExistingFiles from 'hooks/useExistingFiles';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import GlobalTooltip from 'components/GlobalTooltip';
import { useDebounce } from 'hooks/useDebounce';
import { sanitizeHtmlColorContent } from 'utils/utils';
import { LoaderComponent } from 'shared/LoaderComponent';
import useFetchFiles from 'hooks/useFetchFiles';
import CustomMessageScheduleOverlayPanel from 'components/CutomMessageScheduleOverlayPanel/CustomMessageScheduleOverlayPanel';
import { convertDateFromApiToUserTimezone } from 'utils/utility_functions/timezone';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';

function InputContainer({ messageDetails, hasPermission, addUserHandler, fetchCaseMessageDetails }) {
  const history = useHistory();

  const userContext = useContext(UserDetailsContext);
  const websocketContext = useContext(WebSocketConnectionContext);
  const setLoader = useLoaderDispatch();
  const { isConnected } = useWebSocketContext();
  const { addToast } = useToast();
  // const maxFiles = 20;

  const {
    isLeadSpecific,
    caseDetails,
    isFromCaseSpecificModule,
    isFromClientPortal,
    onMountUnMount,
    onOperationBreak,
    setMessageScheduleData,
  } = useSecureMessageContext();
  const { files: fetchedExistingFiles, getFiles, handleSetFiles, filesLoading } = useFetchFiles();
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isReply, setIsReply] = useState(false);
  const [showSchedulePanel, setShowSchedulePanel] = useState(false);
  const [panelPosition, setPanelPosition] = useState('below');
  const [scheduleData, setScheduleData] = useState();
  const op = useRef(null);
  const modalContainerRef = useRef(null);

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors, dirtyFields },
    resetField,
    clearErrors,
    watch: useWatch,
  } = useForm();

  useEffect(() => {
    if (!isReply) {
      setFiles([]);
    }
  }, [isReply]);

  useEffect(() => {
    if (messageDetails?.auto_save_res?.message) {
      setIsReply(true);
      setValue('replyMessage', messageDetails.auto_save_res.message);
    }
  }, [messageDetails, setValue]);

  useEffect(() => {
    isReply &&
      !isFromClientPortal &&
      typeof onMountUnMount === 'function' &&
      onMountUnMount('message-reply', 'mounted', caseDetails?.case_id);
    return () => {
      isReply && !isFromClientPortal && typeof onOperationBreak === 'function' && onOperationBreak();
    };
  }, [isReply]);

  const watchedReplyMessage = useWatch({
    control,
    name: 'replyMessage',
  });

  const debouncedMessage = useDebounce(watchedReplyMessage, 1000);
  const prevSavedValueRef = useRef('');

  useEffect(() => {
    if (dirtyFields.replyMessage && debouncedMessage.replyMessage !== prevSavedValueRef.current.replyMessage) {
      autosave(debouncedMessage);
      prevSavedValueRef.current = debouncedMessage;
    }
  }, [debouncedMessage]);

  useEffect(() => {
    if (messageDetails?.case_id) {
      const obj = { case_id: messageDetails?.case_id, all_files: true };
      getFiles(obj);
    }
  }, [messageDetails?.case_id]);

  const autosave = async (data, is_delete = false) => {
    try {
      let sendData = {
        is_delete,
        action: 'autosave',
        type: 'autosave_message_reply',
        user_id: userContext.firmDetails.current_user,
        tenant_id: userContext.firmDetails.tenant_id,
        auto_save_type: 'reply',
        message_thread_id: messageDetails.message_thread_id,
        case_id: messageDetails.case_id,
        data: {},
      };
      userContext.firmDetails.firm_user_id && (sendData.firm_user_id = userContext.firmDetails.firm_user_id);
      userContext.firmDetails.contact_id && (sendData.contact_id = userContext.firmDetails.contact_id);
      if (data?.replyMessage) sendData.data.message = data.replyMessage ?? '';
      await websocketContext.sendMessage(sendData);
    } catch (error) {
      console.log(error);
    }
  };

  const reply = async (data) => {
    let messageSent = false;
    try {
      setIsLoading(true);
      const tenantId = getTenantIdFromLS();

      let sendData = {};

      let finalTask = [];
      let finalEvents = [];

      let attachments = await fileUpload();
      if (data.existing_files && data.existing_files.length > 0) {
        attachments = [...attachments, ...data.existing_files];
      }
      sendData.action = 'message';
      sendData.type = 'secure_message';

      sendData.data = {};
      sendData.data.secure_message_type = 'reply';
      sendData.data.tenant_id = tenantId;
      let userInfo = JSON.parse(localStorage.getItem('userInfo'));
      if (userInfo && userInfo.attributes) {
        sendData.creator_first_name = userInfo.attributes['custom:first_name'];
        sendData.creator_last_name = userInfo.attributes['custom:last_name'];
        sendData.creator_email = userInfo.attributes['email'];
      }

      sendData.data.message_thread_id = messageDetails.message_thread_id;

      sendData.data.body = {};
      sendData.data.body.message = data.replyMessage ?? '';
      sendData.data.body.attachment = attachments;
      sendData.data.body.event_attachment = finalEvents;
      sendData.data.body.task_attachment = finalTask;
      sendData.caseId = messageDetails.case_id;
      sendData.case_id = messageDetails.case_id;

      sendData.data.body.case_id = messageDetails.case_id;
      sendData.data.body.case_name = messageDetails.case_name;
      sendData.data.body.scheduled_send_at = scheduleData?.datetime;

      if (scheduleData) {
        setMessageScheduleData(scheduleData);
      }
      await websocketContext.sendMessage(sendData);
      messageSent = true;

      try {
        !isFromClientPortal &&
          typeof onMountUnMount === 'function' &&
          onMountUnMount('message-reply', 'unmounted', messageDetails?.case_id);

        if (scheduleData) {
          setScheduleData(null);
          addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, 'Reply scheduled successfully');
        } else {
          addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, 'Reply sent successfully');
        }
      } catch (postSendErr) {
        console.log('Post-send operation error:', postSendErr);
      }

      setIsLoading(false);
      setIsReply(false);
      resetField('replyMessage');
      resetField('existing_files');
      setFiles([]);
      if (messageDetails?.auto_save_res) {
        messageDetails.auto_save_res.message = '';
      }

      // Refresh messages after a short delay to ensure backend has processed the message
      setTimeout(() => {
        if (fetchCaseMessageDetails) {
          fetchCaseMessageDetails();
        }
      }, 500);
    } catch (err) {
      // If message was sent but we still got here, close the reply area
      if (messageSent) {
        setIsReply(false);
        resetField('replyMessage');
        resetField('existing_files');
        setFiles([]);
        if (messageDetails?.auto_save_res) {
          messageDetails.auto_save_res.message = '';
        }
      }
      !isFromClientPortal && typeof onOperationBreak === 'function' && onOperationBreak();
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, err?.message || 'Failed to send reply');
      console.log(err);
      setIsLoading(false);
    }
  };

  async function fileUpload() {
    if (!files?.length) {
      setLoader(false);
      return [];
    }
    setLoader(true);
    const caseId = isLeadSpecific ? caseDetails?.case_id : history.location.pathname.split('/').reverse()[0];
    let userId = '';
    try {
      const user = await Auth.currentAuthenticatedUser();
      userId = user?.attributes?.sub || '';
    } catch (_) {}
    try {
      const attachments = await uploadFilesV2(files, {
        module: 'messages',
        caseId: caseId || undefined,
        userId: userId || undefined,
        category: UPLOAD_CATEGORY.MESSAGES,
        messageThreadId: messageDetails?.message_thread_id,
      });
      if (messageDetails?.case_id) {
        const obj = { case_id: messageDetails?.case_id, all_files: true };
        getFiles(obj);
      }
      return attachments;
    } catch (e) {
      console.error(e);
      return [];
    } finally {
      setLoader(false);
    }
  }

  return (
    <div className="row mt-2 email-quill">
      {isReply ? (
        <>
          <div className="col-sm-12 mb-2 ">
            <Controller
              control={control}
              name="replyMessage"
              rules={{ required: files.length > 0 ? false : true }}
              render={({ field: { onChange, value } }) => (
                <TextSnippetWrapper value={sanitizeHtmlColorContent(value)} type="editor" onChange={(e) => onChange(e)} />
              )}
            />
            {errors?.replyMessage && <InputErrorMessage>Please enter your message</InputErrorMessage>}
          </div>

          <div className="">
            <ul className="list-unstyled">
              {files &&
                files.length > 0 &&
                files.map((val, index) => (
                  <div className="d-flex align-items-center" key={index}>
                    <i className="fas fa-paperclip me-2 mt-1"></i>
                    <li className="text-break ms-1">{val.name}</li>
                    <i
                      className="icon-delete ms-3 pointer"
                      onClick={() => {
                        let removedFiles = files.filter((data) => data.path !== val.path);
                        setFiles(removedFiles);
                      }}
                    ></i>
                  </div>
                ))}
            </ul>
            <div className="col-12 px-2 drag-drop-box d-flex flex-wrap justify-content-center align-items-center">
              <FileDropzone
                value={files}
                onChange={(updated) => setFiles(updated)}
                accept="application/pdf, application/msword, .docx, .doc, audio/*, video/*, image/*, .heic, .heif, image/heic, image/heif, .xls, .xlsx"
                // maxFiles={maxFiles}
                placeholder="Click or drag file to this area to upload"
              />
              <p className="mb-0 d-flex align-items-center">
                <i>or</i>
              </p>
              <div className="w-100 text-center">
                <Controller
                  name="existing_files"
                  control={control}
                  render={({ field: { name, value, onChange } }) => (
                    <MultiSelect
                      options={
                        filesLoading
                          ? []
                          : fetchedExistingFiles?.length === 0
                          ? [
                              {
                                label: 'No Records Found',
                                value: 'no-records-found',
                              },
                            ]
                          : fetchedExistingFiles.map((item) => ({
                              label: item.display_name,
                              value: {
                                file_path: item?.file_path,
                                size: item?.size,
                                display_name: item?.display_name,
                                file_name: item?.file_name,
                                file_type: item?.file_type,
                                created_by: item?.created_by_details,
                                is_new_file: true,
                                sk: item?.sk,
                                pk: item?.pk,
                              },
                            }))
                      }
                      className="input-shadow existing-files"
                      placeholder="Select Existing Files"
                      display="chip"
                      value={Array.isArray(value) ? value : []}
                      filter
                      onChange={(e) => {
                        onChange(e.value);
                      }}
                      disabled={!messageDetails.case_id}
                      optionLabel="label"
                      optionValue="value"
                      optionDisabled={(index) => index.value === 'no-records-found'}
                      showClear
                      resetFilterOnHide
                      id={name}
                      emptyFilterMessage="No records found"
                      showSelectAll={false}
                      panelFooterTemplate={filesLoading ? LoaderComponent : null}
                    />
                  )}
                />
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-end align-items-center mt-3">
            <div className="scheduled-button-group d-flex position-relative overflow-visible">
              <Button
                type="button"
                label="Cancel"
                className="p-button p-button-secondary outline me-2"
                onClick={() => {
                  setIsReply(!isReply);
                  autosave(null, true);
                  resetField('replyMessage');
                  resetField('existing_files');
                }}
              />
              {isFromClientPortal ? (
                <div className="schedule-button-group">
                  <Button
                    type="button"
                    label={'Send'}
                    className="p-button p-component p-button-secondary"
                    disabled={isLoading}
                    loading={isLoading}
                    onClick={handleSubmit(reply)}
                  />
                </div>
              ) : (
                <div className="schedule-button-group">
                  <Button
                    type="button"
                    label={scheduleData ? 'Schedule' : 'Send'}
                    className="p-button p-button-secondary schedule-main-btn"
                    disabled={isLoading}
                    loading={isLoading}
                    onClick={handleSubmit(reply)}
                  />
                  <div
                    className={`schedule-dropdown-btn${showSchedulePanel ? ' panel-open' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();

                      // Always open upwards
                      setPanelPosition('above');

                      setShowSchedulePanel((prevState) => {
                        const newState = !prevState;
                        return newState;
                      });
                    }}
                  >
                    {/* White vertical separator */}
                    <div className="schedule-separator"></div>
                    <i className="pi pi-chevron-down schedule-chevron"></i>
                  </div>
                </div>
              )}
              <CustomMessageScheduleOverlayPanel
                ref={op}
                visible={showSchedulePanel}
                onHide={() => setShowSchedulePanel(false)}
                style={{
                  position: 'absolute',
                  top: 'auto',
                  bottom: '100%',
                  right: '0',
                  zIndex: 1000,
                  marginTop: '0',
                  marginBottom: '8px',
                }}
                scheduleData={scheduleData}
                setScheduleData={setScheduleData}
              />
            </div>
          </div>
          {scheduleData?.datetime && (
            <div className="mt-2 f-size13 text-hint justify-content-end d-flex">
              Scheduled to send:{' '}
              {convertDateFromApiToUserTimezone(
                scheduleData?.datetime,
                userContext?.userDetails?.timezone,
                constants.month_date_year_time_12_format
              )}
            </div>
          )}
        </>
      ) : (
        <div className="col-sm-12 text-end border-top">
          {userContext.getPermission('messages') === constants.permissions.WRITE && hasPermission && (
            <GlobalTooltip tooltip={hasPermission ? '' : 'You are not part of this thread'}>
              <Button
                label={isConnected ? 'Reply' : 'Connecting'}
                className="btn-outline p-button-secondary py-1 px-3 mt-3"
                type="button"
                onClick={() => setIsReply(!isReply)}
                disabled={!hasPermission || !isConnected}
              />
            </GlobalTooltip>
          )}
          {userContext.getPermission('messages') === constants.permissions.WRITE && !hasPermission && (
            <GlobalTooltip tooltip={hasPermission ? '' : 'Add myself to this conversation'}>
              <Button
                label="Add"
                className="btn-outline p-button-secondary py-1 px-3 mt-3"
                type="button"
                onClick={() => addUserHandler()}
              />
            </GlobalTooltip>
          )}
        </div>
      )}
    </div>
  );
}

export default InputContainer;
